import { el, append } from "../ui/dom";
import { signal, effect } from "../ui/signals";
import {
  pageHeader,
  prose,
  card,
  callout,
  codeBlock,
  rangeControl,
  stat,
  statRow,
  button,
} from "../ui/components";
import { plot, PALETTE } from "../ui/charts";
import { themeColors, h2, caption, ticker } from "./shared";
import { DDSketch } from "../core/dd-sketch";
import * as sizing from "../core/sizing";
import { formatBytes, formatCount, formatPercent } from "../ui/format";
import type { PageRenderer } from "../ui/router";

export const renderDDSketch: PageRenderer = (outlet) => {
  append(
    outlet,
    pageHeader({
      eyebrow: "Quantiles · p50 / p95 / p99",
      title: "DDSketch",
      lede: "Track percentiles — median latency, p99 tail — over an endless stream, within a guaranteed relative error, using memory that grows with the value range, not the item count. And the sketches merge, so per-host p99s roll up into a global one.",
    }),
  );

  append(
    outlet,
    prose(
      el(
        "p",
        {},
        "Storing every latency to compute a p99 doesn't scale. DDSketch buckets values ",
        el("em", {}, "geometrically"),
        ": with γ = (1+α)/(1−α), a value v goes in bucket ⌈log",
        el("sub", {}, "γ"),
        " v⌉, which covers (γ",
        el("sup", {}, "i−1"),
        ", γ",
        el("sup", {}, "i"),
        "]. Reporting that bucket's representative value is provably within ",
        el("strong", {}, "±α"),
        " of anything it holds. So a fat-tailed distribution spanning microseconds to seconds needs only a few hundred buckets — and because the bucket boundaries are shared, two sketches ",
        el("strong", {}, "merge by adding counts"),
        ".",
      ),
    ),
  );

  playgroundCard(outlet);

  append(outlet, h2("Use it"));
  append(
    outlet,
    codeBlock(
      `import { DDSketch } from "./core";

const latency = new DDSketch({ relativeAccuracy: 0.01 }); // ±1%
for (const ms of requestDurations) latency.add(ms);

latency.quantile(0.5);   // median
latency.quantile(0.99);  // p99 tail, within ±1%

// Roll per-host sketches up into a global p99 — no re-scan.
const global = new DDSketch({ relativeAccuracy: 0.01 });
for (const hostSketch of shards) global.merge(hostSketch);
global.quantile(0.999);`,
    ),
  );

  append(
    outlet,
    callout(
      "info",
      el(
        "div",
        {},
        el("strong", {}, "Relative, not absolute, error. "),
        "A ±1% guarantee means the p99 of a slow endpoint and the p50 of a fast one are each tight in proportion — the sketch spends its buckets where the data is, across every order of magnitude. That's what makes it right for latency, where the interesting action is in the tail.",
      ),
    ),
  );
};

// ---------------------------------------------------------------------------

function gaussian(): number {
  const u1 = Math.max(1e-12, Math.random());
  const u2 = Math.random();
  return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
}
function sampleLatency(): number {
  let v = Math.exp(3.9 + 0.5 * gaussian()); // median ≈ 49ms
  if (Math.random() < 0.02) v *= 8 + 40 * Math.random(); // tail spikes
  return v;
}
function fmtMs(v: number): string {
  if (!isFinite(v)) return "—";
  if (v >= 1000) return `${(v / 1000).toFixed(2)}s`;
  if (v >= 10) return `${v.toFixed(0)}ms`;
  return `${v.toFixed(1)}ms`;
}

function playgroundCard(outlet: HTMLElement): void {
  const alphaPct = signal(1); // relative accuracy in percent
  const { tick, bump } = ticker();

  const all: number[] = [];
  let sorted: number[] = [];
  let sortedDirty = true;
  let sketch = new DDSketch({ relativeAccuracy: alphaPct.value / 100 });

  function sortedData(): number[] {
    if (sortedDirty) {
      sorted = [...all].sort((a, b) => a - b);
      sortedDirty = false;
    }
    return sorted;
  }
  function trueQuantile(q: number): number {
    const s = sortedData();
    if (s.length === 0) return NaN;
    if (q <= 0) return s[0];
    if (q >= 1) return s[s.length - 1];
    return s[Math.round(q * (s.length - 1))];
  }

  // Rebuild the sketch when accuracy changes (data is unchanged).
  effect(() => {
    const alpha = alphaPct.value / 100;
    sketch = new DDSketch({ relativeAccuracy: alpha });
    for (const v of all) sketch.add(v);
    bump();
  });

  function feed(n: number): void {
    for (let i = 0; i < n; i++) {
      const v = sampleLatency();
      all.push(v);
      sketch.add(v);
    }
    sortedDirty = true;
    bump();
  }
  function reset(): void {
    all.length = 0;
    sortedDirty = true;
    sketch = new DDSketch({ relativeAccuracy: alphaPct.value / 100 });
    bump();
  }

  const histHost = el("div", { class: "viz-wrap" });
  const curveHost = el("div", { class: "viz-wrap" });
  const tableHost = el("div", {});

  effect(() => {
    tick.value;
    const c = themeColors();
    const hasData = sketch.count > 0;

    // Bucket histogram (geometric buckets → log x).
    const hist = sketch.histogram().filter((b) => b.value > 0);
    histHost.replaceChildren(
      plot({
        height: 250,
        xScale: "log",
        xLabel: "value (log-spaced buckets)",
        yLabel: "count",
        xFormat: fmtMs,
        yFormat: formatCount,
        series: [{ type: "bar", points: hist.map((b) => [b.value, b.count]), color: PALETTE[2], opacity: 0.8 }],
      }),
    );

    // Estimated vs true quantile function.
    const qs: number[] = [];
    for (let q = 0.02; q <= 0.9; q += 0.04) qs.push(q);
    for (const q of [0.92, 0.95, 0.97, 0.99, 0.995, 0.999]) qs.push(q);
    const estPts: Array<[number, number]> = hasData ? qs.map((q) => [q, sketch.quantile(q)]) : [];
    const truePts: Array<[number, number]> = hasData ? qs.map((q) => [q, trueQuantile(q)]) : [];
    curveHost.replaceChildren(
      plot({
        height: 280,
        yScale: "log",
        xLabel: "quantile q",
        yLabel: "value",
        xDomain: [0, 1],
        xFormat: (v) => v.toFixed(2),
        yFormat: fmtMs,
        series: [
          { type: "line", points: truePts, color: c.textFaint, strokeWidth: 4, opacity: 0.5, label: "true" },
          { type: "line", points: estPts, color: PALETTE[0], strokeWidth: 2, label: "DDSketch" },
        ],
        legend: true,
      }),
    );

    // Percentile table.
    const rows = [0.5, 0.9, 0.99, 0.999].map((q) => {
      const est = sketch.quantile(q);
      const tru = trueQuantile(q);
      const err = hasData && isFinite(tru) && tru > 0 ? Math.abs(est - tru) / tru : 0;
      return { q, est, tru, err };
    });
    tableHost.replaceChildren(
      el(
        "table",
        { class: "cmp" },
        el("thead", {}, el("tr", {}, ...["Quantile", "True", "Estimate", "Rel. error"].map((h) => el("th", {}, h)))),
        el(
          "tbody",
          {},
          ...rows.map((r) =>
            el(
              "tr",
              {},
              el("td", {}, `p${Number((r.q * 100).toFixed(1))}`),
              el("td", {}, hasData ? fmtMs(r.tru) : "—"),
              el("td", { style: { color: "var(--accent)" } }, hasData ? fmtMs(r.est) : "—"),
              el("td", {}, hasData ? formatPercent(r.err) : "—"),
            ),
          ),
        ),
      ),
    );
  });

  const controls = el(
    "div",
    { class: "lab-controls" },
    el(
      "div",
      { class: "button-row" },
      button({ label: "+10K requests", variant: "primary", onClick: () => feed(10000) }),
      button({ label: "+50K requests", variant: "ghost", onClick: () => feed(50000) }),
      button({ label: "Reset", variant: "ghost", onClick: reset }),
    ),
    rangeControl({
      label: "relative accuracy α",
      min: 0.25,
      max: 10,
      step: 0.25,
      signal: alphaPct,
      format: (v) => `±${v}%`,
      hint: "tighter accuracy → more buckets",
    }),
    statRow(
      stat({ label: "Requests", value: () => (tick.value, formatCount(sketch.count)) }),
      stat({ label: "Buckets", accent: true, value: () => (tick.value, String(sketch.bucketCount())) }),
    ),
    statRow(
      stat({
        label: "p99 latency",
        value: () => (tick.value, sketch.count ? fmtMs(sketch.quantile(0.99)) : "—"),
      }),
      stat({
        label: "Sketch memory",
        value: () => (tick.value, formatBytes(sizing.ddSketchMemoryBytes(sketch.bucketCount()))),
      }),
    ),
    stat({
      label: "vs. storing every value",
      value: () => {
        tick.value;
        if (!sketch.count) return "—";
        const exact = sketch.count * 8;
        const skc = sizing.ddSketchMemoryBytes(sketch.bucketCount());
        return `${(exact / skc).toFixed(0)}× smaller`;
      },
      hint: "8 bytes per stored double",
    }),
  );

  const viz = el(
    "div",
    {},
    histHost,
    caption("The sketch's geometric buckets — dense where the data is, sparse in the tail. This is all it stores."),
    curveHost,
    caption("The estimated quantile function sits on top of the exact one across the whole range, tail included."),
    tableHost,
  );

  append(
    outlet,
    card({
      title: "Percentiles over a latency stream",
      subtitle: "Feed a fat-tailed latency distribution and read off p50…p999 — then tighten α and watch buckets (and accuracy) trade off.",
      children: [el("div", { class: "lab-layout" }, controls, viz)],
    }),
  );
}
